import React from "react";
import * as translator from "../../../../../utils/translator";
import { MyAccountStoreProduct } from "../types";

interface Props {
  language: string;
  translationPrefix: string;
  product: MyAccountStoreProduct;
  tagDimensions: string[];
  isOrdering: boolean;
  onOrderNow: (product: MyAccountStoreProduct) => void;
  onMoreInfo: (product: MyAccountStoreProduct) => void;
  onTagClick: (product: MyAccountStoreProduct) => void;
}

// `price` is already in major currency units (euros, e.g. 49.99) — same as the
// CMS Test Lab card, which renders `product_item_prices.price` directly. Do NOT
// divide by 100.
const formatPrice = (price: number, currency: string): string => {
  const formatted = price.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return currency === "EUR" ? `${formatted} €` : `${formatted} ${currency}`;
};

const DIMENSION_PILL_BG: Record<string, string> = {
  biomarkers: "bg-[#f7f7f7]",
  symptoms: "bg-[#edfff4]",
};


const countFor = (
  product: MyAccountStoreProduct,
  dimensionKey: string,
): number => {
  const dimension = product.dimensions.find(
    (d: { dimensionKey: string; values: string[] }) =>
      d.dimensionKey === dimensionKey,
  );

  return dimension?.values.length ?? 0;
};

const MyAccountStoreProductCard: React.FC<Props> = ({
  language,
  translationPrefix,
  product,
  tagDimensions,
  isOrdering,
  onOrderNow,
  onMoreInfo,
  onTagClick,
}) => {
  const t = (key: string): string =>
    translator.translate(language, "my_account", `${translationPrefix}/${key}`);

  const orderNowLabel = t("card/orderNow");
  const moreInfoLabel = t("card/moreInfo");
  const stockLabel = product.inStock ? t("card/inStock") : t("card/outOfStock");
  const brandLabel = product.brand || t("card/defaultBrand");

  const openPopup = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onTagClick(product);
  };

  const orderNow = (e: React.MouseEvent): void => {
    e.stopPropagation();

    if (!product.inStock || isOrdering) return;

    onOrderNow(product);
  };

  return (
    <div
      className="group cursor-pointer overflow-hidden rounded-[6px] border border-[#eef1f6] bg-white shadow-none transition-shadow hover:shadow-md sm:rounded-[4px] sm:border-[#f0f2f5] sm:shadow-sm"
      onClick={() => onMoreInfo(product)}
    >
      {/* Top section */}
      <div className="flex flex-col sm:h-[188px] sm:flex-row">
        {/* Image */}
        <div className="relative flex h-[180px] w-full shrink-0 items-center justify-center overflow-hidden px-0 pb-0 pt-[28px] sm:h-[188px] sm:w-[280px] sm:items-start sm:pt-[34px]">          <span
            className={`absolute left-0 top-3 z-10 inline-flex items-center rounded-r-[5px] px-3 py-1 text-xs font-medium sm:hidden ${
              product.inStock
                ? "bg-[#e9fbeb] text-[#02a414]"
                : "bg-gray-100 text-gray-500"
            }`}
          >
            {stockLabel}
          </span>

          {product.imageSrc ? (
            <img
              src={product.imageSrc}
              alt={product.title}
              className="mix-blend-darken h-full w-full object-contain"
              loading="lazy"
            />
          ) : (
            <span className="py-16 text-sm text-gray-300">
              {t("card/noImage")}
            </span>
          )}
        </div>

        {/* Details */}
        <div className="flex min-w-0 flex-1 flex-col px-4 pb-4 pt-1 sm:px-0 sm:pb-0 sm:pl-[28px] sm:pr-5 sm:pt-[34px]">
          <div className="mb-3 flex flex-wrap items-center gap-2 sm:mb-[11px]">
            <span
              className={`hidden items-center rounded-[5px] px-3 py-[5px] text-xs font-medium leading-none sm:inline-flex ${
                product.inStock
                  ? "bg-[#e9fbeb] text-[#02a414]"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {stockLabel}
            </span>

            {tagDimensions.map((dimensionKey) => {
              const count = countFor(product, dimensionKey);

              if (count === 0) return null;

              const label = t(`card/dimensionPill/${dimensionKey}`);
              const pillBg = DIMENSION_PILL_BG[dimensionKey] ?? "bg-[#edfff4]";

              return (
                <button
                  key={dimensionKey}
                  type="button"
                  className={`inline-flex appearance-none items-center rounded-[5px] border-0 px-3 py-[5px] text-xs font-medium leading-none text-[#269c6e] outline-none ${pillBg}`}
                  onClick={openPopup}
                >
                  {count} {label}
                </button>
              );
            })}
          </div>

          <h3 className="text-[20px] font-medium leading-[1.3] text-gray-900 group-hover:text-[#269c6e] sm:text-[21px] sm:font-semibold sm:leading-[26px]">
            {product.title}
          </h3>

          <p className="font-roboto mt-[5px] text-sm text-[#656d79] sm:font-header sm:text-[14px] sm:leading-[20px] sm:text-gray-500">
            {brandLabel}
          </p>

          <p className="mt-[10px] flex items-baseline gap-2 sm:mt-[10px]">
            <span className="text-[22px] font-semibold text-gray-900 sm:text-[22px] sm:font-bold sm:leading-[26px]">
              {formatPrice(product.price, product.currency)}
            </span>

            {product.priceBeforeDiscount != null &&
              product.priceBeforeDiscount > product.price && (
                <span className="text-sm font-normal text-[#9c0505] line-through">
                  {formatPrice(product.priceBeforeDiscount, product.currency)}
                </span>
              )}
          </p>
        </div>
      </div>

      {/* Bottom action bar */}
      <div className="flex flex-col items-center gap-3 border-t border-[#f1f1f1] px-4 py-4 sm:h-[76px] sm:flex-row-reverse sm:items-center sm:justify-between sm:px-[30px] sm:py-0">
        <button
          type="button"
          className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-solid border-[#1a9868] bg-white px-8 transition-colors hover:bg-[#e8f6ee] disabled:cursor-not-allowed disabled:border-gray-300 disabled:hover:bg-transparent sm:w-[205px] sm:px-0"          disabled={!product.inStock || isOrdering}
          onClick={orderNow}
        >
          <span
            className={
              !product.inStock || isOrdering
                ? "text-base font-semibold text-gray-400"
                : "bg-clip-text text-base font-semibold text-transparent"
            }
            style={
              !product.inStock || isOrdering
                ? undefined
                : {
                    backgroundImage:
                      "linear-gradient(90deg, #269c6e 0%, #7aba47 100%)",
                  }
            }
          >
            {isOrdering ? t("card/ordering") : orderNowLabel}
          </span>
        </button>

        <button
          type="button"
          className="border-0 bg-transparent bg-clip-text p-0 text-sm font-medium text-transparent underline decoration-[#1a9868] [background-image:linear-gradient(163deg,#1a9868,#008935)] hover:underline sm:bg-clip-border sm:text-[15px] sm:text-[#1a9868] sm:no-underline sm:[background-image:none]"
          onClick={(e) => {
            e.stopPropagation();
            onMoreInfo(product);
          }}
        >
          {moreInfoLabel}
        </button>
      </div>
    </div>
  );
};

export default MyAccountStoreProductCard;